import { useEffect, useState } from 'react'

import { useAppKitWallet } from '@reown/appkit-wallet-button/react'
import { getWallet } from '@reown/web-wallet-core'

import { useAppKitContext } from '../../context/AppKitContext'

type UseWalletOptions = {
  redirectUrl?: string
  disconnectOnInit?: boolean
}

type UseWalletResult = {
  wallet: Awaited<ReturnType<typeof getWallet>> | undefined
  error: string | undefined
  isInitializing: boolean
  isReady: boolean | undefined
}

export function useWallet(options?: UseWalletOptions): UseWalletResult {
  const { isReady, connect: connectWallet } = useAppKitWallet()
  const { appkit, appKitWalletButton } = useAppKitContext()
  const [wallet, setWallet] = useState<Awaited<ReturnType<typeof getWallet>> | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)
  const [isInitializing, setIsInitializing] = useState(true)
  const redirectUrl = options?.redirectUrl || window.location.origin

  useEffect(() => {
    const initWallet = async () => {
      if (!appkit || !isReady) return

      try {
        const walletInstance = await getWallet({
          appkit,
          appKitWalletButton: connectWallet,
          appKitUpdateEmail: appKitWalletButton.updateEmail,
          redirectUrl: redirectUrl + '/wc',
          disconnectOnInit: options?.disconnectOnInit,
          analyticsUrl: import.meta.env.VITE_PULSE_URL
        })

        if (!walletInstance) {
          setError('Failed to initialize wallet')
          return
        }

        setWallet(walletInstance)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize wallet')
      } finally {
        setIsInitializing(false)
      }
    }

    initWallet()
  }, [appkit, isReady, connectWallet, redirectUrl])

  return {
    wallet,
    error,
    isInitializing,
    isReady
  }
}
